// Dentist (ทันตแพทย์) helpers — shared by the Manager › ทันตแพทย์ list + drawer.
// Backend: Xano Doctor table (api:Bh0CIPqU), read through /api/doctors?slim=1
// (the whole list, projected) and /api/doctors/:id (one full record).
//
// The list is filtered CLIENT-SIDE on purpose. Xano's GET /doctor searches only
// code/phone/name, takes a single `spec` value, and has no tri-state active
// filter (displayall=false → active only, true → everything) — so a server round
// trip per keystroke would be both slower and less capable than filtering the
// ~600 records we already hold.

export { activeInfo } from './employee.js'

/* ── spec (ความเชี่ยวชาญ) ────────────────────────────────────────────────────
 * `spec[]` is a free-text list. It is MOSTLY the appointmentList `type` values,
 * but live data also carries a stat_type (`d-aligner`) and a payer tag (`สปสช`),
 * so the filter's options are always derived from the data — this table only
 * decides ORDER and adds a Thai gloss. An unknown token still shows up, sorted
 * after the known ones.                                                        */
const SPEC_META = [
  ['GP',        'ทันตกรรมทั่วไป'],
  ['Ortho',     'จัดฟัน'],
  ['d-aligner', 'ดี-อะไลเนอร์'],
  ['Endo',      'รักษารากฟัน'],
  ['Perio',     'โรคเหงือก'],
  ['Pedo',      'ทันตกรรมเด็ก'],
  ['Oper',      'อุดฟัน'],
  ['Prosth',    'ใส่ฟัน / ครอบฟัน'],
  ['Surg',      'ศัลยกรรมช่องปาก'],
  ['Implant',   'รากฟันเทียม'],
  ['Veneer',    'วีเนียร์'],
  ['OralMed',   'รอยโรคในช่องปาก'],
  ['Occlusion', 'ข้อต่อขากรรไกร'],
  ['สปสช',      'บัตรทอง'],
]
const SPEC_ORDER = new Map(SPEC_META.map(([k], i) => [k, i]))
const SPEC_GLOSS = new Map(SPEC_META)

// Sentinel for "this doctor has no spec at all" — a real bucket, not an absence:
// plenty of legacy records have `spec: []` and they must stay findable.
export const NO_SPEC = '__none__'

export const specList = (d) => (Array.isArray(d?.spec) ? d.spec.filter(Boolean).map(String) : [])
export const specGloss = (token) => SPEC_GLOSS.get(token) || ''
export const specLabel = (token) => (
  token === NO_SPEC ? 'ไม่ระบุ' : (specGloss(token) ? `${token} · ${specGloss(token)}` : token)
)

// Filter options with live counts, known specs first (SPEC_META order), then any
// unknown token alphabetically, then the "ไม่ระบุ" bucket last.
export function specOptions(doctors) {
  const count = new Map()
  let none = 0
  for (const d of doctors || []) {
    const s = specList(d)
    if (!s.length) { none++; continue }
    for (const t of new Set(s)) count.set(t, (count.get(t) || 0) + 1)
  }
  const opts = [...count.entries()]
    .map(([value, n]) => ({ value, label: specLabel(value), count: n }))
    .sort((a, b) => {
      const oa = SPEC_ORDER.has(a.value) ? SPEC_ORDER.get(a.value) : 999
      const ob = SPEC_ORDER.has(b.value) ? SPEC_ORDER.get(b.value) : 999
      return oa - ob || a.value.localeCompare(b.value, 'th')
    })
  if (none) opts.push({ value: NO_SPEC, label: specLabel(NO_SPEC), count: none })
  return opts
}

/* ── names ───────────────────────────────────────────────────────────────── */
// `code` is the clinic's own handle ("ชีส นภกร") and what every other screen
// shows, so it leads here too.
export const doctorCode = (d) => (
  d?.code || buildCode(d) || `หมอ #${d?.id ?? '?'}`
)

// `code` is DERIVED, never typed: "ชื่อเล่น ชื่อ" ("เอก ธนิตย์"), which is how
// every existing record reads and how staff say a doctor's name. Editing the
// nickname or first name rewrites it — that is the only way it changes.
export const buildCode = (d) => [d?.nickname, d?.name].filter(Boolean).join(' ').trim()
export const doctorFullName = (d) => (
  [d?.prefix, [d?.name, d?.surname].filter(Boolean).join(' ')].filter(Boolean).join(' ') || '—'
)

/* ── except (เคสที่ไม่รับ) ───────────────────────────────────────────────────
 * `Doctor.except` is a list of **appointmentList ids**. The two read paths hand
 * it back differently: the LIST (`GET /doctor?displayall=true`) resolves it
 * through an addon that outputs only `{detail}` (no id), while `GET /doctor/{id}`
 * returns the raw int ids. Pass `apptById` (from lib/dentist.js#indexAppointments)
 * to resolve the raw form; without it an unresolved id degrades to "#12".       */
export function exceptList(d, apptById) {
  const raw = Array.isArray(d?.except) ? d.except : []
  return raw.map((e) => {
    if (e && typeof e === 'object') return e.detail || ''
    const row = apptById?.get?.(Number(e))
    return row ? (row.detail || `#${e}`) : `#${e}`
  }).filter(Boolean)
}

// The ids, whatever shape the record came back in. Objects from the list path
// carry no id, so those rows can only be edited after the full record loads.
export function exceptIds(d, apptByDetail) {
  const raw = Array.isArray(d?.except) ? d.except : []
  return raw.map((e) => {
    if (e && typeof e === 'object') return Number(apptByDetail?.get?.(e.detail)?.id) || 0
    return Number(e) || 0
  }).filter(Boolean)
}

/* ── appointmentList — the source of truth for spec + except ──────────────────
 * `spec[]` holds appointmentList **type** strings; `except[]` holds specific
 * appointmentList **ids**. So the two pickers are one catalog read two ways, and
 * the except options are always narrowed to the types the doctor actually does. */
export function indexAppointments(list) {
  const rows = (Array.isArray(list) ? list : (list?.items || [])).filter(Boolean)
  const byId = new Map()
  const byDetail = new Map()
  for (const r of rows) {
    byId.set(Number(r.id), r)
    if (r.detail && !byDetail.has(r.detail)) byDetail.set(r.detail, r)
  }
  return { rows, byId, byDetail }
}

// Every type in the catalog, plus any legacy token already on this doctor that
// the catalog no longer contains (live data holds `d-aligner` and `สปสช`, which
// are not appointmentList types). Dropping those from the options would quietly
// delete them from the record on the next save.
export function specChoices(apptRows, doctor) {
  const seen = new Set()
  const out = []
  for (const r of apptRows || []) {
    const t = String(r?.type || '').trim()
    if (!t || seen.has(t)) continue
    seen.add(t); out.push({ value: t, label: specLabel(t), legacy: false })
  }
  for (const t of specList(doctor)) {
    if (seen.has(t)) continue
    seen.add(t); out.push({ value: t, label: `${specLabel(t)} (นอกรายการ)`, legacy: true })
  }
  return out.sort((a, b) => {
    const oa = SPEC_ORDER.has(a.value) ? SPEC_ORDER.get(a.value) : 999
    const ob = SPEC_ORDER.has(b.value) ? SPEC_ORDER.get(b.value) : 999
    return oa - ob || a.value.localeCompare(b.value, 'th')
  })
}

// The appointmentList rows a doctor may opt OUT of — only those whose type is in
// their spec. With no spec chosen there is nothing to opt out of, which is the
// honest answer rather than offering the whole catalog.
export function exceptChoices(apptRows, specs) {
  const want = specs instanceof Set ? specs : new Set(specs || [])
  if (!want.size) return []
  return (apptRows || [])
    .filter((r) => want.has(String(r?.type || '').trim()))
    .map((r) => ({ value: Number(r.id), label: `${r.type} · ${r.detail}`, hint: r.detail }))
    .sort((a, b) => a.label.localeCompare(b.label, 'th'))
}

// Deselecting a spec must drop the excepts that hung off it, or the record keeps
// opting out of treatments the doctor no longer claims — invisible in the UI
// (the chip is gone) but still filtering them out of the booking calendar.
export function pruneExcept(exceptIdList, apptById, specs) {
  // No catalog yet = no basis to judge. Pruning here would silently drop EVERY
  // except (nothing resolves), so the safe answer is to change nothing.
  if (!apptById || !apptById.size) return exceptIdList || []
  const want = specs instanceof Set ? specs : new Set(specs || [])
  return (exceptIdList || []).filter((id) => {
    const row = apptById.get(Number(id))
    return row ? want.has(String(row.type || '').trim()) : false
  })
}

/* ── search ──────────────────────────────────────────────────────────────── */
// Staff type what they say out loud — "หมอชีส", "ชีส", "นภกร", a licence number.
// Normalizing both sides (lower-case, collapse spaces, drop a leading หมอ/ทพ.)
// keeps all of those working against one haystack.
export function normText(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/^(หมอ|ทพญ\.|ทพ\.|dr\.?)\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function haystack(d) {
  return normText([
    d?.code, d?.nickname, d?.name, d?.surname, d?.name_eng, d?.prefix,
    d?.license, d?.orthocode, d?.phone, d?.email,
    ...specList(d),
  ].filter(Boolean).join(' '))
}

/* ── the filter ──────────────────────────────────────────────────────────── */
// active: 'true' | 'false' | '' (any) — same tri-state string the Employee list
// uses, so the two pages behave identically.
export function filterDoctors(doctors, { q = '', specs = new Set(), active = '' } = {}) {
  const needle = normText(q)
  const wantSpec = specs && specs.size > 0
  return (doctors || []).filter((d) => {
    if (active === 'true' && d?.active !== true) return false
    if (active === 'false' && d?.active === true) return false
    if (wantSpec) {
      const s = specList(d)
      const hit = s.length ? s.some((t) => specs.has(t)) : specs.has(NO_SPEC)
      if (!hit) return false
    }
    if (needle && !haystack(d).includes(needle)) return false
    return true
  })
}

// Xano sorts GET /doctor by `code` asc; keep the same order so a doctor sits
// where staff expect them coming from any other screen.
export const byCode = (a, b) => doctorCode(a).localeCompare(doctorCode(b), 'th')

/* ── PII masking ─────────────────────────────────────────────────────────────
 * Shown in full only to HR/Admin (see canViewSalary). Everyone else in the
 * Manager area gets a masked tail — enough to confirm "yes, that's the account
 * on file" without the page handing out an ID card or a bank account number.  */
export function maskTail(value, keep = 4) {
  const s = String(value || '').replace(/\s+/g, '')
  if (!s) return ''
  if (s.length <= keep) return '•'.repeat(s.length)
  return '•'.repeat(Math.min(s.length - keep, 12)) + s.slice(-keep)
}

export function bankLine(bank) {
  if (!bank) return ''
  const parts = [bank.bank_name, bank.bank_num].filter(Boolean)
  return parts.length ? parts.join(' · ') : ''
}

/* ── การรับเงิน ──────────────────────────────────────────────────────────────
 * `cash_receive_option` decides the SHAPE of this section, not just a label:
 *
 *   บัญชีเดิม (and the empty value a brand-new doctor carries) — the normal case.
 *     One account. The option is fixed and not offered as a choice, and the one
 *     set of fields the user fills is MIRRORED into both `bank_track` and
 *     `bank_nontrack`, so the two can never drift apart.
 *
 *   anything else (บัญชีใหม่ / เงินสดที่สนามเป้า) — the doctor splits their money
 *     across two accounts, so both are edited independently and the option is
 *     shown as a select (which is also the way back to บัญชีเดิม).
 */
// Same catalog the Expense/seller side uses (data/banks.js), so a bank is named
// identically wherever it appears.
// `Doctor.bank_track.bank_name` / `bank_nontrack.bank_name` used to be an
// 8-value ENUM; they were changed to plain TEXT in Xano on 2026-08-05 so this
// longer list can be used. On the same day the 64 stored "ทหารไทย" values
// (37 doctors) were migrated to "ทหารไทยธนชาต" — every value in the table now
// matches this catalog verbatim. BankFields still keeps an out-of-catalog stored
// value rather than rewriting it: the column is free text now, so a value from
// outside this list can reappear at any time.
export { THAI_BANKS as BANKS } from '../data/banks.js'
export const CASH_RECEIVE_OPTIONS = ['บัญชีเดิม', 'บัญชีใหม่', 'เงินสดที่สนามเป้า']
export const DEFAULT_CASH_OPTION = 'บัญชีเดิม'

export const isMirroredBank = (option) => !option || option === DEFAULT_CASH_OPTION

export const emptyBank = () => ({ bank_name: '', bank_num: '', bank_branchcode: '' })
export const normBank = (b) => ({
  bank_name: b?.bank_name || '',
  bank_num: b?.bank_num || '',
  bank_branchcode: b?.bank_branchcode || '',
})

// The bank half of a save. Mirrored mode writes ONE object to both columns.
export function bankPatch(option, track, nontrack) {
  const opt = option || DEFAULT_CASH_OPTION
  if (isMirroredBank(opt)) {
    const one = normBank(track)
    return { cash_receive_option: DEFAULT_CASH_OPTION, bank_track: one, bank_nontrack: one }
  }
  return { cash_receive_option: opt, bank_track: normBank(track), bank_nontrack: normBank(nontrack) }
}

/* ── file[] ──────────────────────────────────────────────────────────────────
 * Doctor documents live in the Wasabi bucket `dentistssdc`, one folder per
 * doctor named after `Doctor.code` (the proxy builds the key). In-app display
 * always goes through the proxy, never the stored presigned `url`, which expires.
 */
// Encode each path SEGMENT (the '/' between doctor folder and filename must stay
// a separator). Thai names, spaces, '#' and '&' all appear in real filenames and
// would otherwise truncate or mis-sign the key.
export const dentistFileHref = (f) => (
  f?.location
    ? '/api/dentist-file/' + String(f.location).split('/').map(encodeURIComponent).join('/')
    : (f?.url || '')
)
export const fileTagText = (f) => (Array.isArray(f?.tag) ? f.tag.filter(Boolean).join(' · ') : (f?.tag || ''))

// Trust the recorded MIME first, fall back to the extension — legacy rows
// (Bubble-era, and the hand-made test row) carry junk in `type`.
export const isImageFile = (f) => (
  /^image\//.test(f?.type || '') || /\.(png|jpe?g|gif|webp|bmp|heic)$/i.test(f?.name || '')
)
export const FILE_TAGS = ['ใบประกอบวิชาชีพ', 'บัตรประชาชน', 'วุฒิการศึกษา', 'สัญญา', 'บัญชีธนาคาร', 'อื่นๆ']

export function fmtBytes(n) {
  const b = Number(n || 0)
  if (!b) return ''
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`
  return `${(b / 1024 / 1024).toFixed(1)} MB`
}
